import type { IPassportConfiguration } from '@obvious.tech/constellation'
import { z } from 'zod'

import type { TEntity } from 'src/utils/api'

export type TPassportConfiguration = TEntity<IPassportConfiguration>

const logoIdSchema = z.object({
  logo: z.string().trim().min(1),
})

const getLogoId = (configuration: unknown): string | null => {
  const parsed = logoIdSchema.safeParse(configuration)
  return parsed.success ? parsed.data.logo : null
}

export const selectLogoImageId = (
  passportConfigurations: TPassportConfiguration[],
): string | null => {
  for (const { configuration } of passportConfigurations) {
    const logo = getLogoId(configuration)
    if (logo !== null) return logo
  }

  return null
}

const FILE_SIGNATURES: Array<{ mimeType: string; bytes: number[] }> = [
  {
    mimeType: 'image/png',
    bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  },
  {
    mimeType: 'image/jpeg',
    bytes: [0xff, 0xd8, 0xff],
  },
]

const SIGNATURE_BYTES = Math.max(...FILE_SIGNATURES.map(it => it.bytes.length))

const BASE64_MARKER = 'base64,'

const decodeLeadingBytes = (base64: string): number[] => {
  const quadruplets = Math.ceil(SIGNATURE_BYTES / 3)

  try {
    const binary = atob(base64.slice(0, quadruplets * 4))
    return Array.from(binary, character => character.charCodeAt(0))
  } catch {
    return []
  }
}

const getMimeType = (base64: string): string | null => {
  const bytes = decodeLeadingBytes(base64)

  const signature = FILE_SIGNATURES.find(it =>
    it.bytes.every((byte, index) => bytes[index] === byte),
  )

  return signature?.mimeType ?? null
}

export const toImageSource = (imageData: string): string | null => {
  const markerIndex = imageData.indexOf(BASE64_MARKER)
  const base64 = (
    markerIndex === -1
      ? imageData
      : imageData.slice(markerIndex + BASE64_MARKER.length)
  ).trim()

  const mimeType = getMimeType(base64)
  if (mimeType === null) return null

  return `data:${mimeType};${BASE64_MARKER}${base64}`
}
